import { isEqual } from "lodash-es";
import { capitalizeAndCleanUnderscore, removeUnderscore, toLowerCaseAndCapitalize } from "@utils";
import { FIELD_TYPES } from "@hyro-components/fields/utils";
import { LIST_TYPES, TABS } from "src/components/collections/consts";
import { FIELD_TAB_BUCKETS, getSpecFieldBucket, getSpecFieldTab } from "src/components/collections/assistant-configuration/specFieldTabs.utils";
import {
  BOOLEAN_LABELS,
  CHANGE_KINDS,
  CHANGE_TYPES,
  DIFF_OPS,
  EMPTY_LANGUAGE_VALUE,
  EMPTY_VALUE,
  INSTANCE_FIELD_LABELS,
  KNOWLEDGE_GROUP_KEY,
  KNOWLEDGE_SOURCE_AND_TABLE_ITEM_LABEL,
  KNOWLEDGE_TABLE_ITEM_LABEL,
  LANGUAGE_SEPARATOR,
  LONG_TEXT_LENGTH,
  MAX_DIFF_WORDS,
  PATH_SEPARATOR,
  REVIEW_COPY,
  REVIEW_GROUPS,
  ROW_LABEL_FALLBACK_COLUMN,
  ROW_LABEL_FIELDS,
  SECRET_MASK,
  SECTION_KINDS,
  SETTINGS_ENTRIES,
  SETTINGS_GROUP_KEY,
  SHORT_ID_HEAD,
  SHORT_ID_TAIL
} from "./reviewChanges.consts";

const isPlainObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

export const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;
  if (typeof value === "boolean") return BOOLEAN_LABELS[value];
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join(", ") : EMPTY_VALUE;
  if (isPlainObject(value)) return value.display_key ?? value.name ?? JSON.stringify(value);
  return String(value);
};

const pushSegment = (segments, text, op) => {
  const last = segments.at(-1);
  if (last?.op === op) {
    last.text += text;
  } else {
    segments.push({ text, op });
  }
};

// Word-level diff (longest common subsequence over words with their trailing whitespace).
export const wordDiff = (before = "", after = "") => {
  const a = before.match(/\S+\s*|\s+/g) || [];
  const b = after.match(/\S+\s*|\s+/g) || [];
  if (a.length > MAX_DIFF_WORDS || b.length > MAX_DIFF_WORDS) {
    return [{ text: before, op: DIFF_OPS.DELETED }, { text: after, op: DIFF_OPS.INSERTED }].filter(({ text }) => text);
  }
  const common = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      common[i][j] = a[i] === b[j] ? common[i + 1][j + 1] + 1 : Math.max(common[i + 1][j], common[i][j + 1]);
    }
  }
  const segments = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      pushSegment(segments, a[i++], DIFF_OPS.SAME);
      j++;
    } else if (common[i + 1][j] >= common[i][j + 1]) {
      pushSegment(segments, a[i++], DIFF_OPS.DELETED);
    } else {
      pushSegment(segments, b[j++], DIFF_OPS.INSERTED);
    }
  }
  while (i < a.length) pushSegment(segments, a[i++], DIFF_OPS.DELETED);
  while (j < b.length) pushSegment(segments, b[j++], DIFF_OPS.INSERTED);
  return segments;
};

const languageName = (language) => toLowerCaseAndCapitalize(language);

const optionName = (field, key) => field.options?.find((option) => option.key === key)?.display_key ?? key;

const choiceNames = (field, choices) =>
  (Array.isArray(choices) ? choices : []).map((choice) => optionName(field, choice?.key ?? formatValue(choice?.configuration)));

const perLanguageLines = (values, textOf) =>
  Object.entries(values).map(([language, value]) => `${languageName(language)}: ${textOf(value) || EMPTY_LANGUAGE_VALUE}`).join("\n");

// A field's stored value as the collection page shows it (option names, one line per language, ...).
export const formatFieldValue = (field, value) => {
  switch (field?.type) {
    case FIELD_TYPES.SECRET:
      return value ? SECRET_MASK : EMPTY_VALUE;
    case FIELD_TYPES.MULTI_LANGUAGE_TEXT:
      return isPlainObject(value) ? perLanguageLines(value, (text) => text) : formatValue(value);
    case FIELD_TYPES.ACTION:
      return isPlainObject(value) ? perLanguageLines(value, (action) => action?.text) : formatValue(value);
    case FIELD_TYPES.CHOOSE_ONE:
      return value?.key ? optionName(field, value.key) : EMPTY_VALUE;
    case FIELD_TYPES.CHOOSE_AT_LEAST_ONE:
      return choiceNames(field, value).join(", ") || EMPTY_VALUE;
    case FIELD_TYPES.LIST:
      return Array.isArray(value) ? value.map((item) => formatValue(item?.configuration)).join(", ") || EMPTY_VALUE : formatValue(value);
    default:
      return formatValue(value);
  }
};

const isEmptyValue = (value) => value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

// A value set where there was none is added, one cleared is removed, any other change is edited.
const valueChangeType = (before, after) => {
  if (isEmptyValue(before)) return CHANGE_TYPES.ADDED;
  if (isEmptyValue(after)) return CHANGE_TYPES.REMOVED;
  return CHANGE_TYPES.EDITED;
};

const valueRow = (key, label, before, after) => {
  // A value empty on both sides (null in one, missing in the other) is not a change.
  if (isEqual(before, after) || (isEmptyValue(before) && isEmptyValue(after))) return [];
  const isLong = [before, after].some((value) => typeof value === "string" && value.length > LONG_TEXT_LENGTH);
  const row = { key, changeType: valueChangeType(before, after), label, before: formatValue(before), after: formatValue(after) };
  return [isLong ? { ...row, kind: CHANGE_KINDS.TEXT, segments: wordDiff(before ?? "", after ?? "") } : { ...row, kind: CHANGE_KINDS.SCALAR }];
};

const listRows = (key, label, beforeItems = [], afterItems = []) => {
  const remaining = [...beforeItems];
  const added = afterItems.filter((item) => {
    const index = remaining.indexOf(item);
    if (index === -1) return true;
    remaining.splice(index, 1);
    return false;
  });
  const row = (text, index, changeType) => {
    const before = changeType === CHANGE_TYPES.REMOVED ? text : null;
    const after = changeType === CHANGE_TYPES.ADDED ? text : null;
    return { key: `${key}:${changeType}:${index}`, changeType, kind: CHANGE_KINDS.SCALAR, label, before, after };
  };
  return [
    ...added.map((text, index) => row(text, index, CHANGE_TYPES.ADDED)),
    ...remaining.map((text, index) => row(text, index, CHANGE_TYPES.REMOVED))
  ];
};

// A field is labelled as its page labels it: by its title, else its display key.
const fieldLabel = (field) => field.title || field.display_key;

const byKey = (nodes) => new Map((Array.isArray(nodes) ? nodes : []).map((node) => [node?.key, node]));

const isTableField = (field) => field?.type === FIELD_TYPES.LIST && field?.element_spec?.type === LIST_TYPES.TABLE;

const perLanguageRows = (field, label, before, after, rowsOf) =>
  [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].flatMap((language) =>
    rowsOf(`${field.key}.${language}`, `${label}${LANGUAGE_SEPARATOR}${languageName(language)}`, before?.[language], after?.[language]));

const objectRows = (fields = [], beforeNodes = [], afterNodes = [], prefix = []) => {
  const before = byKey(beforeNodes);
  const after = byKey(afterNodes);

  return fields.flatMap((field) => getFieldRows(field, before.get(field.key), after.get(field.key), prefix));
};

// Rows are labelled by their own name; the path of their parents is their `group`, shown once above them.
const groupOf = (path) => (path.length ? path.join(PATH_SEPARATOR) : null);

// The "field: was → now" rows of one non-table field; nested objects and languages give one row per leaf.
export const getFieldRows = (field, beforeNode, afterNode, prefix = []) => {
  const group = groupOf(prefix);
  return fieldRows(field, beforeNode, afterNode, prefix).map((row) => ({ ...row, group: row.group ?? group }));
};

const fieldRows = (field, beforeNode, afterNode, prefix) => {
  const before = beforeNode?.configuration;
  const after = afterNode?.configuration;
  if (isEqual(before, after)) return [];
  const label = fieldLabel(field);
  switch (field.type) {
    case FIELD_TYPES.SECRET:
      return [{
        key: field.key,
        changeType: valueChangeType(before, after),
        kind: CHANGE_KINDS.SCALAR,
        label,
        before: before ? SECRET_MASK : EMPTY_VALUE,
        after: after ? `${SECRET_MASK} (changed)` : EMPTY_VALUE
      }];
    case FIELD_TYPES.MULTI_LANGUAGE_TEXT:
      return perLanguageRows(field, label, before, after, valueRow);
    case FIELD_TYPES.ACTION:
      return perLanguageRows(field, label, before, after, (key, languageLabel, beforeAction, afterAction) => [
        ...valueRow(key, languageLabel, beforeAction?.text, afterAction?.text),
        ...listRows(`${key}.suggestions`, `${languageLabel}${PATH_SEPARATOR}${INSTANCE_FIELD_LABELS.SUGGESTIONS}`,
          (beforeAction?.suggestions ?? []).map(formatValue), (afterAction?.suggestions ?? []).map(formatValue))
      ]);
    case FIELD_TYPES.CHOOSE_ONE: {
      if (before?.key !== after?.key) {
        return valueRow(field.key, label, optionName(field, before?.key), optionName(field, after?.key));
      }
      const option = field.options?.find(({ key }) => key === after?.key);
      return objectRows(option?.fields, before?.configuration, after?.configuration, [...prefix, fieldLabel(field)]);
    }
    case FIELD_TYPES.CHOOSE_AT_LEAST_ONE:
      return listRows(field.key, label, choiceNames(field, before), choiceNames(field, after));
    case FIELD_TYPES.LIST:
      return listRows(field.key, label,
        (before ?? []).map((item) => formatValue(item?.configuration)),
        (after ?? []).map((item) => formatValue(item?.configuration)));
    case FIELD_TYPES.OBJECT:
      return objectRows(field.fields, before, after, [...prefix, fieldLabel(field)]);
    default:
      return valueRow(field.key, label, before, after);
  }
};

// The changed rows of a table field (a list of objects), matched by row key, cells formatted by column type.
// Columns are headed as the table page heads them.
const recordSection = (field, title, beforeRows = [], afterRows = []) => {
  const columnSpecs = field.element_spec?.fields ?? [];
  const columns = columnSpecs.map(({ key, display_key: label }) => ({ key, label: capitalizeAndCleanUnderscore(label) }));
  const cellsOf = (row, previousRow) => {
    const cells = byKey(row?.configuration);
    const previousCells = byKey(previousRow?.configuration);
    return columnSpecs.map((column) => {
      const value = cells.get(column.key)?.configuration;
      const previous = previousCells.get(column.key)?.configuration;
      const changed = !!previousRow && !isEqual(value, previous);
      return { key: column.key, value: formatFieldValue(column, value), previous: changed ? formatFieldValue(column, previous) : null, changed };
    });
  };
  const before = byKey(beforeRows);
  const after = byKey(afterRows);
  const rows = [];
  after.forEach((row, key) => {
    const previous = before.get(key);
    if (!previous) {
      rows.push({ key, changeType: CHANGE_TYPES.ADDED, cells: cellsOf(row) });
    } else if (!isEqual(row, previous)) {
      rows.push({ key, changeType: CHANGE_TYPES.EDITED, cells: cellsOf(row, previous) });
    }
  });
  before.forEach((row, key) => {
    if (!after.has(key)) rows.push({ key, changeType: CHANGE_TYPES.REMOVED, cells: cellsOf(row) });
  });
  return rows.length ? { kind: SECTION_KINDS.RECORDS, title, columns, rows } : null;
};

const sectionTitle = (field, bucket, generalGrouping, utteranceGrouping) => {
  const grouping = bucket === FIELD_TAB_BUCKETS.GENERAL ? generalGrouping : bucket === FIELD_TAB_BUCKETS.UTTERANCES ? utteranceGrouping : null;
  const mapping = (grouping?.fieldMappings ?? grouping?.utteranceMappings ?? [])
    .find((m) => (m.fieldId ?? m.utteranceId) === field.key);
  return grouping?.groups?.find(({ groupId }) => groupId === mapping?.groupId)?.name ?? null;
};

const generalTab = () => ({ label: TABS.names.general, value: TABS.names.general });

// The changes of one collection instance, placed on the collection page tab (and grouping section)
// they are edited on. Tabs follow the page's order; General comes first.
export const getInstanceTabs = ({ spec, generalGrouping, utteranceGrouping, collectionName, before, after }) => {
  const tabs = new Map();
  const tabFor = ({ label, value }) => {
    if (!tabs.has(value)) tabs.set(value, { label, value, changeCount: 0, sections: [] });
    return tabs.get(value);
  };
  const addRows = (tabInfo, title, rows) => {
    if (!rows.length) return;
    const tab = tabFor(tabInfo);
    let section = tab.sections.find((s) => s.kind === SECTION_KINDS.FIELDS && s.title === title);
    if (!section) {
      section = { kind: SECTION_KINDS.FIELDS, title, rows: [] };
      tab.sections.push(section);
    }
    section.rows.push(...rows);
    tab.changeCount += rows.length;
  };
  const addSection = (tabInfo, section) => {
    if (!section) return;
    const tab = tabFor(tabInfo);
    tab.sections.push(section);
    tab.changeCount += section.rows.length;
  };

  addRows(generalTab(), null, valueRow("display_key", INSTANCE_FIELD_LABELS.NAME, before?.display_key, after?.display_key));
  addRows(generalTab(), null, valueRow("disabled", INSTANCE_FIELD_LABELS.ENABLED, before ? !before.disabled : undefined, after ? !after.disabled : undefined));

  const beforeNodes = byKey(before?.configuration?.configuration);
  const afterNodes = byKey(after?.configuration?.configuration);
  (spec?.spec?.fields ?? []).filter((field) => !field.is_hidden).forEach((field) => {
    const tab = getSpecFieldTab(field, collectionName);
    if (isTableField(field)) {
      addSection(tab, recordSection(field, null, beforeNodes.get(field.key)?.configuration, afterNodes.get(field.key)?.configuration));
      return;
    }
    const bucket = getSpecFieldBucket(field, collectionName);
    addRows(tab, sectionTitle(field, bucket, generalGrouping, utteranceGrouping), getFieldRows(field, beforeNodes.get(field.key), afterNodes.get(field.key)));
  });

  const ordered = [...tabs.values()];
  ordered.forEach((tab) => tab.sections.sort((a, b) => (a.title === null ? -1 : b.title === null ? 1 : 0)));
  return ordered.sort((a, b) => (a.value === TABS.names.general ? -1 : b.value === TABS.names.general ? 1 : 0));
};

const knowledgeRowKey = (row) => Object.values(row.key ?? {}).join(" / ");

const shortId = (id) => (id.length > SHORT_ID_HEAD + SHORT_ID_TAIL + 1 ? `${id.slice(0, SHORT_ID_HEAD)}…${id.slice(-SHORT_ID_TAIL)}` : id);

// Knowledge columns hold multi-language text as { ENGLISH: "...", SPANISH: "..." }.
const isLanguageMap = (value) => isPlainObject(value) && Object.keys(value).length > 0
  && Object.entries(value).every(([key, text]) => /^[A-Z_]+$/.test(key) && (typeof text === "string" || text === null));

const formatKnowledgeValue = (value) => (isLanguageMap(value) ? perLanguageLines(value, (text) => text) : formatValue(value));

// One entry per changed knowledge table (from the knowledge service's knowledge_diff), named after its
// knowledge source; its section shows each changed row whole (its name first, id columns left out, as
// Explore shows them), marking the cells that changed. A row without a name shows as "Unnamed <row type>"
// with its shortened id.
export const getKnowledgeEntries = (tables = [], specRef = []) => tables.map((table) => {
  const rowData = (row) => row.after ?? row.before ?? {};
  const labelField = ROW_LABEL_FIELDS.find((field) => table.rows.some((row) => field in rowData(row)));
  const idFields = new Set(table.rows.flatMap((row) => Object.keys(row.key ?? {})));
  const otherFields = [...new Set(table.rows.flatMap((row) => [...Object.keys(row.before ?? {}), ...Object.keys(row.after ?? {})]))]
    .filter((field) => field !== labelField && !idFields.has(field));
  const columns = [
    labelField ? { key: labelField, label: capitalizeAndCleanUnderscore(labelField) } : ROW_LABEL_FALLBACK_COLUMN,
    ...otherFields.map((key) => ({ key, label: capitalizeAndCleanUnderscore(key) }))
  ];
  const unnamedLabel = REVIEW_COPY.unnamedRow(capitalizeAndCleanUnderscore(table.schema_type).toLowerCase());
  const rows = table.rows.map((row) => ({
    key: knowledgeRowKey(row),
    changeType: row.change_type,
    // Stored values, for reference columns, which show the referenced entity's label as Explore does.
    data: rowData(row),
    previousData: row.before,
    cells: columns.map(({ key }) => {
      if (key === ROW_LABEL_FALLBACK_COLUMN.key) {
        return { key, value: unnamedLabel, previous: null, changed: false, placeholder: true, detail: REVIEW_COPY.rowId(shortId(knowledgeRowKey(row))) };
      }
      const changed = row.change_type === CHANGE_TYPES.EDITED && (row.changed_fields ?? []).includes(key);
      return { key, value: formatKnowledgeValue(rowData(row)[key]), previous: changed ? formatKnowledgeValue(row.before?.[key]) : null, changed };
    })
  }));
  const source = specRef.find((spec) => spec.schema_type === table.schema_type || spec.group === table.schema_type);
  const changeCount = table.added_count + table.removed_count + table.edited_count;
  return {
    key: table.table,
    name: source?.display_key ?? capitalizeAndCleanUnderscore(table.schema_type),
    schemaType: table.schema_type,
    changeCount,
    truncated: table.truncated,
    section: {
      kind: SECTION_KINDS.RECORDS,
      title: capitalizeAndCleanUnderscore(table.table),
      schemaType: table.schema_type,
      baseVersion: table.base_version,
      targetVersion: table.target_version,
      columns,
      rows,
      truncatedCount: changeCount - rows.length
    }
  };
});

const casedWord = (word, index) => {
  if (/^[A-Z0-9]{2,}$/.test(word)) return word;
  const lower = word.toLowerCase();
  return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
};

// A settings key in words: "subAccountId" → "Sub account id", "isSSMLSupported" → "Is SSML supported".
// Keys that are data rather than names (a voice like "en-US-Ava") keep their spelling.
const humanizeKey = (key) => {
  if (!/^[A-Za-z0-9_]+$/.test(key)) return capitalizeAndCleanUnderscore(key);
  const words = key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(" ")
    .filter(Boolean);
  return words.map(casedWord).join(" ");
};

const flatten = (value, path = []) => {
  if (isPlainObject(value)) {
    return Object.entries(value).flatMap(([key, child]) => flatten(child, [...path, key]));
  }
  return [[path, value]];
};

// Settings and languages arrive as raw before/after values; one row per changed leaf, labelled by its key
// under the path of its parents.
export const getValueRows = (valueChange, prefix = []) => {
  if (!valueChange) return [];
  const { before, after } = valueChange;
  if (Array.isArray(before) || Array.isArray(after)) {
    return listRows(prefix.join("."), prefix.join(PATH_SEPARATOR), (before ?? []).map(formatValue), (after ?? []).map(formatValue));
  }
  const beforeLeaves = new Map(flatten(before ?? {}).map(([path, value]) => [path.join("."), value]));
  const afterLeaves = new Map(flatten(after ?? {}).map(([path, value]) => [path.join("."), value]));
  return [...new Set([...beforeLeaves.keys(), ...afterLeaves.keys()])].flatMap((path) => {
    const names = [...prefix, ...path.split(".").map(humanizeKey)];
    return valueRow(path, names.at(-1), beforeLeaves.get(path), afterLeaves.get(path))
      .map((row) => ({ ...row, group: groupOf(names.slice(0, -1)) }));
  });
};

const tabLink = (basePath, tab) => (basePath && tab.value ? `${basePath}?tab=${encodeURIComponent(tab.value)}` : null);

const withLinks = (tabs, basePath) => tabs.map((tab) => ({ ...tab, link: tabLink(basePath, tab) }));

const sumChanges = (tabs) => tabs.reduce((sum, tab) => sum + tab.changeCount, 0);

const valueEntry = ({ key, name, tabLabel }, rows, changeType = CHANGE_TYPES.EDITED) => (rows.length ? {
  key,
  name,
  sub: null,
  changeType,
  changeCount: rows.length,
  link: null,
  tabs: [{ label: tabLabel, value: tabLabel, link: null, changeCount: rows.length, sections: [{ kind: SECTION_KINDS.FIELDS, title: null, rows }] }]
} : null);

// Knowledge table changes join their knowledge source's entry on an Explore tab (one section per table,
// titled only when there are several). A table belongs to the knowledge source of its spec that changed
// in the review (e.g. one just added), else to the listed instance of that spec.
const addKnowledgeTables = ({ entries, tables, knowledgeSources, accountId, assistantId }) => {
  const instances = [...(knowledgeSources?.draftConfigRef ?? []), ...(knowledgeSources?.configRef ?? [])];
  const instanceOf = (schemaType) => {
    const specKeys = (knowledgeSources?.specRef ?? [])
      .filter((spec) => spec.schema_type === schemaType || spec.group === schemaType)
      .map(({ key }) => key);
    const changed = entries.find((entry) => specKeys.includes(entry.specKey));
    if (changed) return { key: changed.instanceKey, spec_key: changed.specKey, display_key: changed.name };
    return instances.find((ref) => specKeys.includes(ref.spec_key));
  };
  const sources = new Map();
  getKnowledgeEntries(tables, knowledgeSources?.specRef).forEach((table) => {
    const instance = instanceOf(table.schemaType);
    const sourceKey = instance?.key ?? table.key;
    const name = instance ? resourceTitle(knowledgeSources?.specRef, instance.spec_key, instance.display_key) : table.name;
    if (!sources.has(sourceKey)) sources.set(sourceKey, { instance, name, tables: [] });
    sources.get(sourceKey).tables.push(table);
  });

  sources.forEach(({ instance, name, tables: sourceTables }, sourceKey) => {
    const sections = sourceTables.map(({ section }) => ({ ...section, title: sourceTables.length > 1 ? section.title : null }));
    const explore = { label: TABS.names.explore, value: TABS.names.explore, changeCount: sections.reduce((sum, { rows }) => sum + rows.length, 0), sections };
    const key = `${KNOWLEDGE_GROUP_KEY}/${sourceKey}`;
    const sourceEntry = entries.find((entry) => entry.key === key);
    if (sourceEntry) {
      const tabs = [...sourceEntry.tabs, ...withLinks([explore], sourceEntry.link)];
      Object.assign(sourceEntry, { sub: KNOWLEDGE_SOURCE_AND_TABLE_ITEM_LABEL, tabs, changeCount: sumChanges(tabs) });
      return;
    }
    const basePath = instance ? `/${accountId}/${assistantId}/knowledge-studio/${instance.spec_key}/${instance.key}` : null;
    entries.push({
      key,
      name,
      sub: KNOWLEDGE_TABLE_ITEM_LABEL,
      changeType: CHANGE_TYPES.EDITED,
      changeCount: explore.changeCount,
      link: basePath,
      tabs: withLinks([explore], basePath)
    });
  });
};

// A resource is named like its card on the collection page: the spec's title, then the resource's own
// name when it differs ("FAQ: Refill").
const resourceTitle = (specRef, specKey, displayKey) => {
  const ref = (specRef ?? []).find(({ key }) => key === specKey);
  const title = (ref?.spec ?? ref)?.display_key;
  if (!title) return displayKey;
  const custom = displayKey && displayKey.trim() !== title.trim() ? removeUnderscore(displayKey) : null;
  return custom ? `${capitalizeAndCleanUnderscore(title)}: ${custom}` : capitalizeAndCleanUnderscore(title);
};

const entryRows = (entry) => entry.tabs.flatMap((tab) => tab.sections.flatMap((section) => section.rows));

// One unit everywhere (filters, group counts, the button): a changed row, or a whole added/removed resource.
const entryChangeTypes = (entry) => (entry.tabs.length ? entryRows(entry).map(({ changeType }) => changeType) : [entry.changeType]);

const withCounts = (key, title, entries) => ({ key, title, entries, count: entries.flatMap(entryChangeTypes).length });

// The review response arranged for the dialog: collection groups → resources → tabs → sections → rows.
// `specs` maps "<collection>/<key>" to the instance's fetched collection (spec + groupings);
// `collections` maps a collection to its listing ({ specRef, configRef, draftConfigRef }).
export const getReviewGroups = ({ review, specs = {}, collections = {}, accountId, assistantId }) => {
  const entriesByGroup = new Map(REVIEW_GROUPS.map(({ key }) => [key, []]));

  (review?.collections ?? []).forEach((change) => {
    const group = REVIEW_GROUPS.find(({ key }) => key === change.collection);
    if (!group) return;
    const collectionData = specs[`${change.collection}/${change.key}`];
    const basePath = change.changeType === CHANGE_TYPES.REMOVED ? null : `/${accountId}/${assistantId}/${group.route}/${change.specKey}/${change.key}`;
    const tabs = change.changeType === CHANGE_TYPES.EDITED ? withLinks(getInstanceTabs({
      spec: collectionData?.spec,
      generalGrouping: collectionData?.generalGrouping,
      utteranceGrouping: collectionData?.utteranceGrouping,
      collectionName: group.route,
      before: change.before,
      after: change.after
    }), basePath) : [];
    entriesByGroup.get(group.key).push({
      key: `${change.collection}/${change.key}`,
      instanceKey: change.key,
      specKey: change.specKey,
      name: resourceTitle(collections[change.collection]?.specRef, change.specKey, change.name),
      sub: group.itemLabel,
      changeType: change.changeType,
      changeCount: tabs.length ? sumChanges(tabs) : 1,
      link: basePath,
      tabs
    });
  });

  addKnowledgeTables({
    entries: entriesByGroup.get(KNOWLEDGE_GROUP_KEY),
    tables: review?.knowledge,
    knowledgeSources: collections[KNOWLEDGE_GROUP_KEY],
    accountId,
    assistantId
  });

  [
    valueEntry(SETTINGS_ENTRIES.CHANNEL, getValueRows(review?.settings), review?.settings?.changeType),
    valueEntry(SETTINGS_ENTRIES.LANGUAGES, getValueRows(review?.languages, [SETTINGS_ENTRIES.LANGUAGES.name]), review?.languages?.changeType)
  ].filter(Boolean).forEach((entry) => entriesByGroup.get(SETTINGS_GROUP_KEY).push(entry));

  return REVIEW_GROUPS
    .map(({ key, title }) => withCounts(key, title, entriesByGroup.get(key)))
    .filter(({ count }) => count > 0);
};

// How many changes there are of each type.
export const countChangeTypes = (groups) => {
  const types = groups.flatMap(({ entries }) => entries.flatMap(entryChangeTypes));
  return {
    all: types.length,
    added: types.filter((type) => type === CHANGE_TYPES.ADDED).length,
    edited: types.filter((type) => type === CHANGE_TYPES.EDITED).length,
    removed: types.filter((type) => type === CHANGE_TYPES.REMOVED).length
  };
};

const filterEntry = (entry, changeTypes) => {
  if (!entry.tabs.length) return changeTypes.includes(entry.changeType) ? entry : null;
  const tabs = entry.tabs
    .map((tab) => {
      const sections = tab.sections
        .map((section) => ({ ...section, rows: section.rows.filter((row) => changeTypes.includes(row.changeType)) }))
        .filter((section) => section.rows.length);
      return { ...tab, sections, changeCount: sections.reduce((sum, section) => sum + section.rows.length, 0) };
    })
    .filter((tab) => tab.sections.length);
  // The resource keeps its total ("Skill · 4 changes"); its tabs count what the filter shows.
  return tabs.length ? { ...entry, tabs } : null;
};

// Only the changes of the selected types (none selected: everything); groups, resources and tabs left empty are dropped.
export const filterReviewGroups = (groups, changeTypes = []) => {
  if (!changeTypes.length) return groups;
  return groups
    .map((group) => withCounts(group.key, group.title, group.entries.map((entry) => filterEntry(entry, changeTypes)).filter(Boolean)))
    .filter(({ count }) => count > 0);
};
